import { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import { useTranslation } from '../state/I18nContext';
import type { TournamentStatus } from '../domain';

interface DeleteTournamentDialogProps {
  open: boolean;
  status: TournamentStatus;
  onClose: () => void;
  onConfirm: () => void;
}

/**
 * Confirms deleting a tournament. A DRAFT (not started) deletes after a single
 * confirmation; a started tournament (IN_PROGRESS / FINISHED) requires a second,
 * explicit confirmation step before the destructive action is allowed.
 */
export function DeleteTournamentDialog({
  open,
  status,
  onClose,
  onConfirm,
}: DeleteTournamentDialogProps) {
  const { t } = useTranslation();
  const started = status !== 'DRAFT';
  // Tracks the second step of the double confirmation for started tournaments.
  const [confirmedOnce, setConfirmedOnce] = useState(false);

  const close = () => {
    setConfirmedOnce(false);
    onClose();
  };

  const confirm = () => {
    if (started && !confirmedOnce) {
      setConfirmedOnce(true);
      return;
    }
    setConfirmedOnce(false);
    onConfirm();
  };

  return (
    <Dialog open={open} onClose={close}>
      <DialogTitle>
        {started
          ? t('admin.deleteTournament.titleStarted')
          : t('admin.deleteTournament.titleDraft')}
      </DialogTitle>
      <DialogContent>
        {!started && (
          <DialogContentText>
            {t('admin.deleteTournament.draftBody')}
          </DialogContentText>
        )}
        {started && !confirmedOnce && (
          <DialogContentText>
            {t('admin.deleteTournament.startedBody')}
          </DialogContentText>
        )}
        {started && confirmedOnce && (
          <Alert severity="error">
            {t('admin.deleteTournament.finalWarning')}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>{t('admin.common.cancel')}</Button>
        <Button color="error" variant="contained" onClick={confirm}>
          {started
            ? confirmedOnce
              ? t('admin.deleteTournament.deletePermanently')
              : t('admin.deleteTournament.continue')
            : t('admin.common.delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
